import React, { useEffect, useState } from 'react'

import update from 'react-addons-update'
import { useToasts } from 'react-toast-notifications'
import styled from 'styled-components'

import { Link, Spacer } from '@atoms/index'
import { ResponsivePXValue, theme } from '@client/components/Theme'
import { scrollToSelector } from '@client/components/utility'
import { useConfig } from '@client/contexts/ConfigProvider'
import { useEvents } from '@contexts/GTMProvider'
import {
  UserAddressFragment, useUserDetailsQuery, useUserCartQuery, GetUnavailableProductsQuery, useRemoveItemFromCartMutation, useRemoveUserAddressMutation, useSaveUserMenuMutation, useUpdateUserAddressMutation, RegisteredUserDetailsFragment, UserDetailsFragment, useGetUnavailableProductsLazyQuery,
} from '@hooks/api'
import { useScrollToTopOnMount } from '@hooks/UseScrollToTopOnMount'
import { SectionLoadingOverlay, UserAddressListItem, Notification } from '@molecules/index'
import { ConfirmDeleteModal, AddressForm, AddressChangeProductWarningModal } from '@organisms/modals'

const ListContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  position: relative;

  .confirm-notification {
    flex: 1;
    text-align: center;
  }
`
const Container = styled.div<{$opacity:string}>`
  display: flex;
  flex-direction: column;
  position: relative;
  opacity:${props => props.$opacity === '1' ? '1' : '0.45'};
`

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 100%;

  .button {
    ${ResponsivePXValue('padding', '0 24px')}
  }
`

interface UserAddressListState {
  loading: boolean
  userAddresses: UserAddressFragment[]
  addressToBeDeleted: UserAddressFragment | null
  addressToBeEdited: UserAddressFragment | null
  addressModalOpen: boolean
  confirmDeleteModalOpen: boolean
  isGuestUser: boolean
  showWarningModal: boolean
  newAddressId: string
  conflictingItemsIds: string[]
  conflictingItems: GetUnavailableProductsQuery
}

const DEFAULT_STATE: UserAddressListState = {
  loading: false,
  userAddresses: [],
  addressToBeDeleted: null,
  addressToBeEdited: null,
  addressModalOpen: false,
  confirmDeleteModalOpen: false,
  isGuestUser: false,
  showWarningModal: false,
  newAddressId: null,
  conflictingItemsIds: [],
  conflictingItems: null,
}

interface UserAddressListProps {
  displayConfirmation?: boolean
}

export function UserAddressList({ displayConfirmation = false }: UserAddressListProps): JSX.Element {

  const config = useConfig()
  const { data, loading } = useUserDetailsQuery({ ssr: config.fetchSSRQuery() })
  const [saveMenu] = useSaveUserMenuMutation()
  // const { data: cartData } = useUserCartQuery({ ssr: config.fetchSSRQuery() })

  const [state, setState] = useState<UserAddressListState>({ ...DEFAULT_STATE })
  const [removeUserAddress] = useRemoveUserAddressMutation()
  const [updateUserAddress] = useUpdateUserAddressMutation()
  const { addToast } = useToasts()
  const [removeItemFromCart] = useRemoveItemFromCartMutation()
  const [getUnavailableProducts, { data: conflictingItems }] = useGetUnavailableProductsLazyQuery()
  const event = useEvents()
  const { data: userCartData } = useUserCartQuery({ ssr: config.fetchSSRQuery() })
  // const { data: userDetailsData } = useUserDetailsQuery({ ssr: config.fetchSSRQuery() })
  const { data: userDetailsData } = useUserDetailsQuery({ ssr: config.fetchSSRQuery() })
  useScrollToTopOnMount()
  const menuId = userCartData?.currentUser?.activeMenu?.id

  const sortAddresses = (a: UserAddressFragment, b: UserAddressFragment) => {
    if (a?.isDefault && !b?.isDefault) return -1
    if (!a?.isDefault && b?.isDefault) return 1
    return 0
  }

  const processNewAddress = async (): Promise<void> => {
    try {
      const { data: conflictingItemsList } = await getUnavailableProducts({
        variables: {
          userAddressId: state.newAddressId,
        },
      })

      const hasUnavailableItems = conflictingItemsList?.unavailableProducts?.length > 0

      const conflictingItems = conflictingItemsList
      const conflictingItemsIds: string[] = []
      for (let i = 0; i < conflictingItems?.unavailableProducts.length; i++) {
        conflictingItemsIds.push(conflictingItems.unavailableProducts[i].id)
      }

      if (hasUnavailableItems) {
        setState((prevState) => update(prevState, {
          showWarningModal: { $set: true },
          conflictingItemsIds: { $set: conflictingItemsIds },
          conflictingItems: { $set: conflictingItemsList },
        }))
      } else {
        await _handleChangeDefaultAddress(state.newAddressId)
      }

    } catch (e) {
      console.log(e)
    } finally {
      setState((prevState) => ({ ...prevState, loading: false }))
    }
  }

  const _handlePopup = (): void => {
    setState((prevState) => update(prevState, {
      addressToBeEdited: { $set: data?.currentUser?.addresses.find((item) => { return item.isDefault }) },
      addressModalOpen: { $set: true },
    }))
  }
  const _handleAddNew = (): void => {
    setState((prevState) => update(prevState, {
      addressModalOpen: { $set: true },
    }))
  }

  const _handleAddressModalClose = (): void => {
    setState((prevState) => update(prevState, {
      addressToBeEdited: { $set: null },
      addressModalOpen: { $set: false },
    }))
  }

  const _handleDeleteModalClose = (): void => {
    setState((prevState) => update(prevState, {
      confirmDeleteModalOpen: { $set: false },
    }))
  }

  const _handleDeleteAddress = (address: UserAddressFragment): void => {
    setState((prevState) => update(prevState, {
      confirmDeleteModalOpen: { $set: true },
      addressToBeDeleted: { $set: address },
    }))
  }

  const _deleteAddress = (): void => {
    setState((prevState) => update(prevState, {
      loading: { $set: true },
      confirmDeleteModalOpen: { $set: false },
    }))
    if (state.addressToBeDeleted) {
      const id = state.addressToBeDeleted.id
      try {
        removeUserAddress({
          variables: {
            id,
          },
          // refetchQueries: [{ query: UserDetailsDocument }, { query: UserCartDocument }],
          // awaitRefetchQueries: true,
        })
        addToast('Address successfully deleted!', {
          appearance: 'success',
          autoDismiss: true,
        })
      } catch (e) {
        addToast(e.message, {
          appearance: 'error',
          autoDismiss: true,
        })
      }
    } else {
      addToast('Address not found!', {
        appearance: 'error',
        autoDismiss: true,
      })
    }
    setState((prevState) => update(prevState, {
      loading: { $set: false },
      addressToBeDeleted: { $set: null },
    }))
  }

  // const getUpdateAddressRefetchQueries = () => state.isGuestUser ? SiteHelper.getCheckoutRefetchQueries() : SiteHelper.getUserRefetchQueries()

  const _handleMakeDefault = async (id: string): Promise<void> => {
    const address = data?.currentUser?.addresses?.find((address) => address.id === id)
    if (address.isIncomplete) {
      addToast('You cannot set an incomplete address as your default address. Please edit and complete the address before setting as default address', {
        appearance: 'warning',
        autoDismiss: true,
      })
      return
    }
    setState((prevState) => update(prevState, {
      loading: { $set: true },
      newAddressId: { $set: id },
    }))

  }

  const _handleChangeDefaultAddress = async (id: string): Promise<void> => {

    try {
      await updateUserAddress({
        variables: {
          id,
          input: {
            isDefault: true,
          },
        },
        // refetchQueries: getUpdateAddressRefetchQueries(), //
        // awaitRefetchQueries: true,
      })

      addToast('Default address successfully updated!', {
        appearance: 'success',
        autoDismiss: true,
      })

    } catch (e) {
      addToast(e.message, {
        appearance: 'error',
        autoDismiss: true,
      })
    }
  }

  const _onCancel = () => {
    setState((prevState) => update(prevState, {
      loading: { $set: false },
      showWarningModal: { $set: false },
    }))
  }

  const _handleWarningModalClose = () => {
    setState((prevState) => update(prevState, {
      loading: { $set: true },
    }))
    _handleRemoveConflictingItemsFromCart(state.conflictingItemsIds)
    _handleChangeDefaultAddress(state.newAddressId)
    _saveUserMenu(menuId)
    setState((prevState) => update(prevState, {
      loading: { $set: false },
      showWarningModal: { $set: false },
    }))
  }

  const _saveUserMenu = async (id: string): Promise<void> => {

    setState((prevState) => update(prevState, {
      loading: { $set: true },
    }))

    try {
      await saveMenu({
        variables: {
          id,
        },
        // refetchQueries: [{ query: UserCartDocument }],
        // awaitRefetchQueries: true,
      })

      const discounts = userCartData?.currentUser?.activeCart?.reductions
      let discountTitles = 'no discount'
      let discountValue = 0

      for (let i = 0; i < discounts.length; i++) {
        discountTitles = discountTitles + discounts[i].title
        discountValue = discountValue + discounts[i].value
      }
      const deliveryFee = userCartData?.currentUser?.activeCart?.additions?.find((addition) => addition.title === 'Delivery Fee')?.value
      const cartItemPrice = userCartData?.currentUser?.activeCart?.grandTotal
      const totalPrice = deliveryFee + cartItemPrice
      event.hasSavedCart(userCartData?.currentUser?.activeCart?.cartItems, totalPrice, discountTitles, discountValue, userDetailsData?.currentUser?.id)

      addToast('Your cart has been saved!', {
        appearance: 'success',
        autoDismiss: true,
      })

    } catch (e) {
      addToast(e.message, {
        appearance: 'warning',
        autoDismiss: true,
      })
    }

    setState((prevState) => update(prevState, {
      loading: { $set: false },
    }))

  }
  const _handleRemoveConflictingItemsFromCart = async (conflictingItemsIds: string[]): Promise<void> => {

    for (let i = 0; i < conflictingItemsIds.length; i++) {
      try {
        await removeItemFromCart({
          variables: {
            productId: conflictingItemsIds[i],
          },
          // refetchQueries: SiteHelper.getUserRefetchQueries(),
          // awaitRefetchQueries: true,
        })
      } catch (err) {
        console.log(err)
      }
    }

  }

  useEffect(() => {
    if (data?.currentUser?.addresses[0]?.isIncomplete) {
      _handlePopup()
    }

    if (data?.currentUser) {
      const registeredUser = data?.currentUser as UserDetailsFragment & RegisteredUserDetailsFragment
      const isGuestUser = data?.currentUser?.__typename === 'GuestUser'
      const userAddresses: UserAddressFragment[] = registeredUser?.addresses as UserAddressFragment[] || []

      // Sort the addresses array based on the selected object
      const sortedAddresses = [...userAddresses].sort(sortAddresses)

      setState((prevState) => update(prevState, {
        userAddresses: { $set: sortedAddresses },
        isGuestUser: { $set: isGuestUser },
        conflictingItems: { $set: conflictingItems },
      }))
    }
  }, [data?.currentUser?.addresses])

  useEffect(() => {
    if (displayConfirmation) {
      setTimeout(() => {
        scrollToSelector('#streetAddressContainer')
      }, 1000)
    }
  }, [displayConfirmation])

  useEffect(() => {
    if (state.newAddressId) {
      processNewAddress()
    }
  }, [state.newAddressId])

  let userAddress: UserAddressFragment

  const _shouldExpandAddress = (userAddress: UserAddressFragment): boolean => displayConfirmation && userAddress.isDefault
  const _addressButtonText = state.userAddresses.length > 0 ? 'Add another Address' : 'Add Address'

  return (
    <ListContainer>
      <ConfirmDeleteModal
        open={state.confirmDeleteModalOpen}
        title='Delete address'
        action='ADDRESS'
        content='You are about to delete your address! Are your sure you want to proceed?'
        onDelete={_deleteAddress}
        onClose={_handleDeleteModalClose} />
      <AddressChangeProductWarningModal
        open={state.showWarningModal}
        title='You are about to change your address'
        content={state.conflictingItems}
        onCancel={_onCancel}
        onConfirm={_handleWarningModalClose} />
      <If condition={displayConfirmation}>
        <Spacer universal='16px' />
        <Notification
          textClassName='confirm-notification'
          text='Please confirm your address'
          backgroundColor={theme.colors.yellows.sunglow} />
        <Spacer universal='16px' />
      </If>
      <Container $opacity={(state.loading || loading) ? '0.45' : '1'}>
        <For each='userAddress' of={state.userAddresses}>
          <UserAddressListItem
            key={userAddress.id}
            userAddress={userAddress}
            loading={state.loading}
            expanded={_shouldExpandAddress(userAddress)}
            onDelete={() => _handleDeleteAddress(userAddress)}
            onMakeDefault={() => _handleMakeDefault(userAddress.id)} />
        </For>
        <Spacer universal='16px' />
        <ButtonContainer>
          <Link
            bold
            decoration='underline'
            // color={theme.colors.oranges.coral}
            className='button'
            onClick={_handleAddNew}>
            {_addressButtonText}
          </Link>
        </ButtonContainer>
        <If condition={state.addressModalOpen}>
          <AddressForm onSuccess={_handleAddressModalClose} onCancel={_handleAddressModalClose} />
        </If>
      </Container>
      <If condition={state.loading || loading} >
        <SectionLoadingOverlay/>
      </If>
    </ListContainer>
  )
}
